import { IColumn } from 'interfaces';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getActivityColumns } from './getActivityColumns';
import { Empty, Table, TableProps } from 'antd';
import classes from './UsersActivityTable.module.scss'
import { useAppSelector } from 'store/hook';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import dayjs from 'dayjs'
import { IUserActivityItem } from 'interfaces/AnalyticTypes';
import analytics from 'api/analytics';

type IUsersActivityTableProps = {
  period: [string, string, string];
  choosenRoom?: string;
}

const UsersActivityTable = ({choosenRoom, period}: IUsersActivityTableProps) => {
  const [modifiedColumns, setModifiedColumns] = React.useState<IColumn[]>([]);
  const [isLoadingUsersActivity, setIsLoadingUsersActivity] = React.useState(false);
  const [usersActivityData, seUsersActivityData] = React.useState<IUserActivityItem[]>([]);

  const { userData } = useAppSelector((store) => store.userData);
  const { messageApi } = useAppSelector((state) => state.windowState);

  const { t, i18n } = useTranslation();

  const getUsersActivity = async (period: [string, string, string], room?: string) => {
    setIsLoadingUsersActivity(true);
    try {
      const tempLinks = await analytics.getAnalyticsUsersActivity({room, from: period[0], to: period[1]});  
      seUsersActivityData(tempLinks.data?.map((el: any, i: number) => ({...el, key: i, last_visit: dayjs.utc(el?.last_visit).valueOf()})));
    } catch (e) {
      messageApi?.error(t('Analytics.errors.users_activity'), 6)
    } finally {
      setIsLoadingUsersActivity(false);
    };
  };

  React.useEffect(() => {
    getUsersActivity(period, choosenRoom);
  }, [choosenRoom, period[2]]);

  React.useEffect(()=> {
    setModifiedColumns(getActualFilters(usersActivityData));
  }, [usersActivityData, i18n.language]);

  const getActualFilters = (dataSource: any) => {
    const logsColumns = getActivityColumns(t);
    const columnsWithFilters = ['user_name', 'email'];

    return logsColumns.map((col: IColumn) => {
      const needFilter = columnsWithFilters.includes(col.dataIndex)
      const unicValues = needFilter ? Array.from(new Set(dataSource.map((log: any) => log[col.dataIndex]))) : [];
      const currentFilters = needFilter ? unicValues.map((value: any) => ({ value: value, text: value })) : [];     
      return {
        ...col,
        render: (value: any, info: any) => renderCell({value, info, col}),
        filters: needFilter && currentFilters,
        onFilter: (value: boolean, record: any) => record[col.dataIndex] === value,
      }
    })
  };

  const renderCell = (props: any) => {
    const { value, info, col } = props;
    if (col.key === 'last_visit'){
      return value && getDateWithTimeZone(value, `${userData?.date_format || 'international'}Date`) + ` (${dayjs(value).fromNow()})`;
    };
    return value
  };

  const onChange: TableProps<IUserActivityItem>['onChange'] = (pagination, filters, sorter, extra) => {    
    if (extra.action === 'filter') {
      setModifiedColumns(getActualFilters(extra.currentDataSource));
    };    
  };

  return (
    <Table
      dataSource={usersActivityData}
      className={classes.activityTable}
      columns={modifiedColumns}
      pagination={false}
      locale={{emptyText: 
        <Empty
          description={ isLoadingUsersActivity
            ? t('Analytics.docsActivity.loading')
            : t('Analytics.docsActivity.empty')
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      }}
      loading={isLoadingUsersActivity}
      onChange={onChange}
      size='small'
      scroll={{
        y: `400px`,
        // x: 'max-content'
      }}
    />
  );
}

export default UsersActivityTable;
